import { useEffect, useState } from "react";
import { api, type EntityData } from "../api.js";
import type { JSX } from "react";

interface Props {
  registerId: string;
  hasData: boolean;
}

export function ExportPanel({ registerId, hasData }: Props): JSX.Element {
  const [entity, setEntity] = useState<EntityData | null>(null);
  const [lei, setLei] = useState("");
  const [refDate, setRefDate] = useState("");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("EUR");

  const leiValid = /^[A-Z0-9]{20}$/.test(lei);
  const leiError: string | null =
    lei.length === 0
      ? null
      : lei.length !== 20
        ? `${lei.length}/20 characters`
        : !/^[A-Z0-9]+$/.test(lei)
          ? "Uppercase letters and digits only"
          : null;

  useEffect(() => {
    api
      .getEntity(registerId)
      .then(({ entity: e }) => {
        if (e !== null) {
          setEntity(e);
          setLei(e.lei);
          setRefDate(e.refDate);
          setCountry(e.country);
        }
      })
      .catch(() => {
        // ignore - entity may not be saved yet
      });
  }, [registerId]);

  void entity; // used via setEntity to pre-fill form fields

  const verifiedZipUrl = (): string => {
    const params = new URLSearchParams({
      lei,
      referenceDate: refDate,
      country,
      baseCurrency: currency,
    });
    return `/api/registers/${registerId}/verified-export.zip?${params.toString()}`;
  };

  return (
    <div>
      {!hasData && (
        <p className="muted" style={{ marginBottom: "1rem" }}>
          No data yet — import and promote arrangements via the Import tab first.
        </p>
      )}
      <section className="card sample-path">
        <h2>Sample Export (DORA register package)</h2>
        <p className="muted">
          Downloads a ZIP package of the live register. Marked as{" "}
          <strong>not for submission</strong> until the taxonomy entrypoint is pinned.
        </p>
        <div className="actions">
          <a
            className="button ghost"
            data-testid="export-zip"
            href={!hasData ? undefined : api.zipUrl(registerId)}
            onClick={!hasData ? (e) => e.preventDefault() : undefined}
            title={!hasData ? "Import and promote data first" : undefined}
            style={{
              pointerEvents: !hasData ? "none" : undefined,
              opacity: !hasData ? 0.45 : undefined,
            }}
          >
            Download SAMPLE ZIP
          </a>
        </div>
      </section>

      <section className="card verified-path">
        <h2>Verified Export (EBA v4.0)</h2>
        <p className="muted">
          Generates a verified ZIP from your template data. Requires entity fields below. Marked as{" "}
          <strong>not for submission</strong> — structural conformance to the v4.0 taxonomy is not
          confirmation of NCA acceptance.
        </p>
        <form
          data-testid="verified-export-form"
          onSubmit={(e) => {
            e.preventDefault();
            window.location.href = verifiedZipUrl();
          }}
        >
          <label>
            Entity LEI
            <span style={{ color: "var(--bad)", marginLeft: "2px" }} aria-label="required">
              *
            </span>
            <input
              type="text"
              value={lei}
              onChange={(e) => setLei(e.target.value)}
              placeholder="20-char LEI"
              maxLength={20}
              style={{
                display: "block",
                margin: "0.25rem 0 0.75rem",
                padding: "0.4rem 0.6rem",
                border:
                  lei.length > 0
                    ? leiValid
                      ? "1px solid #4ade80"
                      : "1px solid #f87171"
                    : "1px solid #cbd5e1",
                borderRadius: "4px",
                fontSize: "0.875rem",
                width: "100%",
                maxWidth: "320px",
              }}
            />
            {leiError !== null && (
              <span
                style={{
                  color: "#f87171",
                  fontSize: "0.78rem",
                  display: "block",
                  marginTop: "-0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                {leiError}
              </span>
            )}
            {lei.length > 0 && leiError === null && (
              <span
                style={{
                  color: "#4ade80",
                  fontSize: "0.78rem",
                  display: "block",
                  marginTop: "-0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                ✓ Valid LEI format
              </span>
            )}
          </label>
          <label>
            Reference Date
            <span style={{ color: "var(--bad)", marginLeft: "2px" }} aria-label="required">
              *
            </span>
            <input
              type="date"
              value={refDate}
              onChange={(e) => setRefDate(e.target.value)}
              style={{
                display: "block",
                margin: "0.25rem 0 0.75rem",
                padding: "0.4rem 0.6rem",
                border: "1px solid #cbd5e1",
                borderRadius: "4px",
                fontSize: "0.875rem",
                width: "100%",
                maxWidth: "320px",
              }}
            />
            <small
              style={{
                display: "block",
                color: "var(--muted)",
                fontSize: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              Reporting reference date — typically quarter or year-end (e.g. 2024-12-31)
            </small>
          </label>
          <label>
            Country
            <span style={{ color: "var(--bad)", marginLeft: "2px" }} aria-label="required">
              *
            </span>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              style={{
                display: "block",
                margin: "0.25rem 0 0.75rem",
                padding: "0.4rem 0.6rem",
                border: "1px solid #cbd5e1",
                borderRadius: "4px",
                fontSize: "0.875rem",
                width: "100%",
                maxWidth: "320px",
                background: "#0b1219",
                color: "var(--ink)",
              }}
            >
              <option value="">— select country —</option>
              <option value="AT">AT — Austria</option>
              <option value="BE">BE — Belgium</option>
              <option value="BG">BG — Bulgaria</option>
              <option value="CH">CH — Switzerland</option>
              <option value="CY">CY — Cyprus</option>
              <option value="CZ">CZ — Czech Republic</option>
              <option value="DE">DE — Germany</option>
              <option value="DK">DK — Denmark</option>
              <option value="EE">EE — Estonia</option>
              <option value="ES">ES — Spain</option>
              <option value="FI">FI — Finland</option>
              <option value="FR">FR — France</option>
              <option value="GB">GB — United Kingdom</option>
              <option value="GR">GR — Greece</option>
              <option value="HR">HR — Croatia</option>
              <option value="HU">HU — Hungary</option>
              <option value="IE">IE — Ireland</option>
              <option value="IS">IS — Iceland</option>
              <option value="IT">IT — Italy</option>
              <option value="LI">LI — Liechtenstein</option>
              <option value="LT">LT — Lithuania</option>
              <option value="LU">LU — Luxembourg</option>
              <option value="LV">LV — Latvia</option>
              <option value="MT">MT — Malta</option>
              <option value="NL">NL — Netherlands</option>
              <option value="NO">NO — Norway</option>
              <option value="PL">PL — Poland</option>
              <option value="PT">PT — Portugal</option>
              <option value="RO">RO — Romania</option>
              <option value="SE">SE — Sweden</option>
              <option value="SI">SI — Slovenia</option>
              <option value="SK">SK — Slovakia</option>
            </select>
          </label>
          <label>
            Base Currency
            <span style={{ color: "var(--bad)", marginLeft: "2px" }} aria-label="required">
              *
            </span>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              style={{
                display: "block",
                margin: "0.25rem 0 0.75rem",
                padding: "0.4rem 0.6rem",
                border: "1px solid #cbd5e1",
                borderRadius: "4px",
                fontSize: "0.875rem",
                width: "100%",
                maxWidth: "320px",
                background: "#0b1219",
                color: "var(--ink)",
              }}
            >
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="CHF">CHF — Swiss Franc</option>
              <option value="SEK">SEK — Swedish Krona</option>
              <option value="DKK">DKK — Danish Krone</option>
              <option value="NOK">NOK — Norwegian Krone</option>
              <option value="PLN">PLN — Polish Zloty</option>
              <option value="CZK">CZK — Czech Koruna</option>
              <option value="HUF">HUF — Hungarian Forint</option>
              <option value="RON">RON — Romanian Leu</option>
              <option value="BGN">BGN — Bulgarian Lev</option>
              <option value="HRK">HRK — Croatian Kuna</option>
              <option value="USD">USD — US Dollar</option>
            </select>
          </label>
          <p style={{ fontSize: "0.75rem", color: "var(--muted)", margin: "4px 0 8px" }}>
            * Required
          </p>
          <button
            type="submit"
            disabled={!leiValid || !hasData || country === "" || refDate === ""}
          >
            Download Verified ZIP
          </button>
        </form>
      </section>
    </div>
  );
}
